import React from 'react';
import { clsx } from '../helpers/clsx.js';

type ColSpan = number;

interface SHXColProps extends React.HTMLAttributes<HTMLDivElement> {
  xs?: ColSpan;
  sm?: ColSpan;
  md?: ColSpan;
  lg?: ColSpan;
  xl?: ColSpan;
  xxl?: ColSpan;

  /**
   * Specialty property used for setting how wide this col should be in comparison to others
   *
   * Should not be mixed and matched with xs/sm/... properties. It's either or:
   *
   * - Use xs/sm/... for specific sizes
   * - use grow for proportional sizing (all these cols should be 1, but this one should be 2, meaning wider)
   */
  grow?: number;

  /**
   * If `grow` has been provided, setting a `minWidth` will allow the col to properly wrap, when it gets too small.
   *
   * Internally capped at 100% of the row's width, so a col can never force horizontal overflow on narrow screens.
   */
  minWidth?: number | string;
}

const spanClass = (bp: string, span: ColSpan) => (bp === 'xs' ? `shx-col-${span}` : `shx-col-${bp}-${span}`);

const capMinWidth = (minWidth: number | string) => `min(${typeof minWidth === 'number' ? `${minWidth}px` : minWidth}, 100%)`;

/**
 * Similar to react-bootstrap `Col` except that sizes are relative to Row's width, as opposed to viewport size
 */
export const SHXCol = React.forwardRef<HTMLDivElement, SHXColProps>(
  ({ xs, sm, md, lg, xl, xxl, className, children, grow, minWidth, style, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'shx-col',
          xs !== undefined && spanClass('xs', xs),
          sm !== undefined && spanClass('sm', sm),
          md !== undefined && spanClass('md', md),
          lg !== undefined && spanClass('lg', lg),
          xl !== undefined && spanClass('xl', xl),
          xxl !== undefined && spanClass('xxl', xxl),
          className
        )}
        style={{
          flexGrow: grow,
          flexBasis: minWidth !== undefined ? capMinWidth(minWidth) : undefined,
          minWidth: minWidth !== undefined ? capMinWidth(minWidth) : undefined,
          ...style,
        }}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SHXCol.displayName = 'SHXCol';

type SHXRowProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * Similar to react-bootstrap `Row` except that sizes of Cols are relative to Row's width, as opposed to viewport size
 */
export const SHXRow = React.forwardRef<HTMLDivElement, SHXRowProps>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={clsx('shx-row', className)} {...props}>
      {children}
    </div>
  );
});
SHXRow.displayName = 'SHXRow';
